// axios instances & API functions
// src/api/admin.ts
import axios from 'axios';

// Configure axios base URL to point to your FastAPI backend
// Use same-origin (empty string) for all requests in production
// Development mode will use localhost through proxy in package.json
// Cache-bust v53: Force browser to download new JavaScript file
const API_BASE_URL = '';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export interface Patient {
  patient_id: string;           // String ID generated by backend
  serial_number: number;        // Sequential serial number assigned by backend
  first_name: string;           // Required in backend
  last_name: string;            // Required in backend  
  date_of_birth?: string;       // Optional datetime
  gender?: string;
  family_status?: string;       // Enum in backend
  number_of_children?: number;
  education_level?: string;     // Enum in backend
  occupation?: string;
  residence_type?: string;
  ethnicity?: string;
  // Contact Information
  email?: string;               // Optional email
  amka?: string;                // Optional AMKA
  landline_country_code?: string; // Optional landline country code
  landline_phone?: string;      // Optional landline phone
  mobile_country_code?: string; // Optional mobile country code
  mobile_phone?: string;        // Optional mobile phone
  // Medical/Psychiatric History
  previous_psychiatric_diagnoses?: string;
  psychiatric_hospitalizations?: string;
  medication_history?: string;
  suicide_history?: string;
  family_psychiatric_history?: string;
  substance_use_history?: string;
  chronic_medical_conditions?: string;
  allergies?: string;
  other_significant_diagnoses?: string;
  current_medical_monitoring?: string;
  // Psychosocial
  support_network?: string;     // Enum in backend
  financial_status?: string;    // Enum in backend
  living_conditions?: string;   // Enum in backend
  relationships?: string;       // Enum in backend
  trauma_history?: string;
  religious_beliefs?: string;
  // Visit Reason
  main_complaint?: string;
  symptom_duration?: string;
  precipitating_factors?: string;
  treatment_progress?: string;
}

export type PatientInput = Omit<Patient, 'patient_id' | 'serial_number'>;

// API Response wrapper to match backend StandardResponse
interface StandardResponse<T> {
  status_code: number;
  message: string;
  data?: T;
}

// Attach token dynamically before each request
apiClient.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses globally
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid, clear it and redirect to login
      localStorage.removeItem('token');
      // Optionally redirect to login page
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export async function createPatient(data: PatientInput): Promise<Patient> {
  // Backend now generates patient_id automatically, so we don't send it
  const patientData = {
    ...data,
    // Remove patient_id - backend will generate it automatically
    // Convert date string to ISO datetime if provided
    date_of_birth: data.date_of_birth ? new Date(data.date_of_birth).toISOString() : undefined,
    // Ensure numeric fields are properly typed
    number_of_children: data.number_of_children ? Number(data.number_of_children) : undefined,
  };
  
  // Remove undefined values to avoid validation issues
  Object.keys(patientData).forEach(key => {
    if (patientData[key as keyof typeof patientData] === undefined || patientData[key as keyof typeof patientData] === '') {
      delete patientData[key as keyof typeof patientData];
    }
  });
  
  console.log('Sending patient data:', patientData); // Debug log
  
  const res = await apiClient.post<StandardResponse<Patient>>('/patients', patientData);
  if (res.data.data) {
    return res.data.data;
  }
  throw new Error('Failed to create patient');
}

export async function updatePatient(id: string, data: PatientInput): Promise<Patient> {
  // Apply the same data processing as createPatient
  const patientData = {
    ...data,
    // Convert date string to ISO datetime if provided
    date_of_birth: data.date_of_birth ? new Date(data.date_of_birth).toISOString() : undefined,
    // Ensure numeric fields are properly typed
    number_of_children: data.number_of_children ? Number(data.number_of_children) : undefined,
  };
  
  // Remove undefined values to avoid validation issues
  Object.keys(patientData).forEach(key => {
    if (patientData[key as keyof typeof patientData] === undefined || patientData[key as keyof typeof patientData] === '') {
      delete patientData[key as keyof typeof patientData];
    }
  });
  

  
  const res = await apiClient.put<StandardResponse<Patient>>(`/patients/${id}`, patientData);
  if (res.data.data) {
    return res.data.data;
  }
  throw new Error('Failed to update patient');
}

export async function getPatient(id: string): Promise<Patient> {
  const res = await apiClient.get<StandardResponse<Patient>>(`/patients/${id}`);
  if (res.data.data) {
    return res.data.data;
  }
  throw new Error('Failed to get patient');
}

export async function getAllPatients(): Promise<Patient[]> {
  const res = await apiClient.get<StandardResponse<Patient[]>>('/patients');
  if (res.data.data) {
    return res.data.data;
  }
  throw new Error('Failed to get patients');
}

export async function deletePatient(id: string): Promise<void> {
  const res = await apiClient.delete<StandardResponse<null>>(`/patients/${id}`);
  if (res.data.status_code === 200) {
    return;
  }
  throw new Error(res.data.message || 'Failed to delete patient');
}

export async function assignQuestionnaireToPatient(
  patientId: string, 
  questionnaireId: string
): Promise<{ assignmentId: string; link: string }> {
  // For now, this is a mock implementation
  // In a real implementation, this would create an assignment record in the database
  const assignmentId = `assignment_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  const link = `/questionnaire/${questionnaireId}/${patientId}`;
  
  // TODO: Replace with actual API call when backend endpoint is ready
  // const res = await apiClient.post<StandardResponse<any>>(`/patients/${patientId}/questionnaires/${questionnaireId}`);
  
  return { assignmentId, link };
}

export async function checkQuestionnaireStatus(
  patientId: string, 
  questionnaireId: string
): Promise<{ isCompleted: boolean; status: string; completedAt?: string; createdAt?: string; scores?: any }> {
  try {
    const res = await apiClient.get<StandardResponse<any>>(
      `/patients/${patientId}/questionnaires/${questionnaireId}/status`
    );
    
    if (res.data.data) {
      return {
        isCompleted: res.data.data.status === 'completed',
        status: res.data.data.status,
        completedAt: res.data.data.updated_at,
        createdAt: res.data.data.created_at,
        scores: res.data.data.scores
      };
    }
    
    return { isCompleted: false, status: 'not_started' };
  } catch (error: any) {
    // 404 means no response found (not started)
    if (error.response?.status === 404) {
      return { isCompleted: false, status: 'not_started' };
    }
    
    // For other errors, log them but still return not_started
    console.warn(`Failed to check questionnaire status for patient ${patientId}, questionnaire ${questionnaireId}:`, error);
    return { isCompleted: false, status: 'not_started' };
  }
}

// New interface for questionnaire statistics by category
export interface QuestionnaireStatsByCategory {
  [questionnaireId: string]: {
    completedCount: number;
    inProgressCount: number;
    lastCompletedAt: string | null;
  };
}

export async function getQuestionnaireStatsByCategory(
  patientId: string
): Promise<QuestionnaireStatsByCategory> {
  try {
    const res = await apiClient.get<StandardResponse<{
      patient_id: string;
      statistics: QuestionnaireStatsByCategory;
    }>>(`/api/patients/${patientId}/questionnaires/stats-by-category`);
    
    if (res.data.data?.statistics) {
      return res.data.data.statistics;
    }
    
    // Return empty stats if no data
    return {
      aq: { completedCount: 0, inProgressCount: 0, lastCompletedAt: null },
      eq: { completedCount: 0, inProgressCount: 0, lastCompletedAt: null },
      hamilton: { completedCount: 0, inProgressCount: 0, lastCompletedAt: null },
      zung: { completedCount: 0, inProgressCount: 0, lastCompletedAt: null },
      scid: { completedCount: 0, inProgressCount: 0, lastCompletedAt: null }
    };
  } catch (error: any) {
    console.warn(`Failed to get questionnaire stats for patient ${patientId}:`, error);
    
    // Return empty stats on error
    return {
      aq: { completedCount: 0, inProgressCount: 0, lastCompletedAt: null },
      eq: { completedCount: 0, inProgressCount: 0, lastCompletedAt: null },
      hamilton: { completedCount: 0, inProgressCount: 0, lastCompletedAt: null },
      zung: { completedCount: 0, inProgressCount: 0, lastCompletedAt: null },
      scid: { completedCount: 0, inProgressCount: 0, lastCompletedAt: null }
    };
  }
}

// Email functionality
export interface QuestionnaireEmailData {
  questionnaire_id: string;
  name: string;
  link: string;
  description?: string;
}

export interface SendEmailRequest {
  patient_id: string;
  patient_email: string;
  questionnaires: QuestionnaireEmailData[];
  language: string;
}

export async function sendQuestionnaireEmail(data: SendEmailRequest): Promise<{ success: boolean; message: string; sent_to: string; questionnaires_sent: number }> {
  const res = await apiClient.post<StandardResponse<{ success: boolean; sent_to: string; patient_name: string; questionnaires_sent: number; language: string }>>(
    '/admin/email/send-questionnaires',
    data
  );
  
  if (res.data.data) {
    return {
      success: res.data.data.success,
      message: res.data.message,
      sent_to: res.data.data.sent_to,
      questionnaires_sent: res.data.data.questionnaires_sent
    };
  }
  
  throw new Error(res.data.message || 'Failed to send email');
}

export interface SendWelcomeEmailRequest {
  patient_email: string;
  patient_name: string;
  registration_link: string;
  doctor_name?: string;
  clinic_name?: string;
  estimated_duration?: string;
  expiry_time?: string;
  link_expiry?: string;
  language: string;
}

export async function sendWelcomeEmail(data: SendWelcomeEmailRequest): Promise<{ success: boolean; message: string; sent_to: string }> {
  const res = await apiClient.post<StandardResponse<{ success: boolean; sent_to: string; patient_name: string; language: string }>>(
    '/admin/email/send-welcome',
    data
  );
  
  if (res.data.data) {
    return {
      success: res.data.data.success,
      message: res.data.message,
      sent_to: res.data.data.sent_to
    };
  }
  
  throw new Error(res.data.message || 'Failed to send welcome email');
}

// Registration API Functions

/**
 * Patient Registration Form Data - Clean interface for patient self-registration
 * Only includes fields actually used in the PatientRegistrationForm component
 * Aligned with Patient schema for proper database storage
 */
export interface RegistrationFormData {
  // Basic Information (Required section)
  first_name: string;
  last_name: string;
  date_of_birth?: string;
  gender?: string;
  ethnicity?: string;
  amka?: string;
  
  // Contact Information (Required section)
  email?: string;
  landline_country_code?: string;
  landline_phone?: string;
  mobile_country_code?: string;
  mobile_phone?: string;
  address?: string; // Note: Not in Patient schema - consider removing or adding to schema
  
  // Family & Social Information (Optional section)
  family_status?: string;
  number_of_children?: number;
  education_level?: string;
  occupation?: string;
  residence_type?: string;
  
  // Medical/Psychiatric History (Required section)
  previous_psychiatric_diagnoses?: string;
  psychiatric_hospitalizations?: string;
  medication_history?: string;
  suicide_history?: string;
  family_psychiatric_history?: string;
  substance_use_history?: string;
  chronic_medical_conditions?: string;
  allergies?: string;
  other_significant_diagnoses?: string;
  current_medical_monitoring?: string;
  
  // Psychosocial Information (Optional section)
  support_network?: string;
  financial_status?: string;
  living_conditions?: string;
  relationships?: string;
  trauma_history?: string;
  religious_beliefs?: string;
  
  // Current Visit Information (Required section)
  main_complaint?: string;
  symptom_duration?: string;
  precipitating_factors?: string;
  treatment_progress?: string;
}

/**
 * Extended Patient Data - Legacy interface for backwards compatibility
 * Keep for any existing code that might still reference it
 * @deprecated Use RegistrationFormData for new registration forms
 */
export interface ExtendedPatientData extends RegistrationFormData {
  // Keep the legacy interface for backwards compatibility
  // but inherit from the cleaner RegistrationFormData
  patient_id?: string;
  
  // Legacy/unused fields (kept for backwards compatibility)
  phone?: string;
  medical_history?: string;
  medications?: string;
  family_medical_history?: string;
  insurance_provider?: string;
  insurance_number?: string;
  insurance_type?: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  emergency_contact_relationship?: string;
  smoking_status?: string;
  alcohol_consumption?: string;
  exercise_frequency?: string;
  employment_status?: string;
  notes?: string;
}

export interface RegistrationTokenResponse {
  registration_token: string;
  expires_at: string;
  patient_id: string;
  language: string;
  registration_url: string;
  email_sent?: boolean;
  sent_to?: string;
  patient_name?: string;
}

export interface RegistrationValidation {
  valid: boolean;
  error?: string;
  token_info?: {
    patient_id: string;
    current_data: RegistrationFormData;
    form_progress: Record<string, boolean>;
    language: string;
    expires_at: string;
    status: string;
  };
}

export interface RegistrationStatus {
  found: boolean;
  error?: string;
  status?: string;
  completed?: boolean;
  progress?: Record<string, boolean>;
  progress_percentage?: number;
  expires_at?: string;
  last_saved?: string;
  language?: string;
}

export async function createRegistrationToken(
  patientData: PatientInput, 
  language: string = 'en'
): Promise<RegistrationTokenResponse> {
  const token = localStorage.getItem('token');
  
  const res = await apiClient.post(
    '/admin/patients/registration-token',
    {
      patient_data: patientData,
      language: language
    },
    {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    }
  );
  
  if (res.data.data) {
    return res.data.data;
  }
  
  throw new Error(res.data.message || 'Failed to create registration token');
}

export async function sendWelcomeWithRegistration(
  patientData: PatientInput,
  language: string,
  doctorName: string,
  clinicName: string,
  estimatedDuration: string = '15-20 minutes'
): Promise<RegistrationTokenResponse> {
  const token = localStorage.getItem('token');
  
  // Apply the same data processing as createPatient to avoid validation issues
  const processedPatientData = {
    ...patientData,
    // Convert date string to ISO datetime if provided
    date_of_birth: patientData.date_of_birth ? new Date(patientData.date_of_birth).toISOString() : undefined,
    // Ensure numeric fields are properly typed
    number_of_children: patientData.number_of_children ? Number(patientData.number_of_children) : undefined,
  };
  
  // Remove undefined values to avoid validation issues, but preserve important fields for patient registration
  const fieldsToPreserve = ['email', 'date_of_birth', 'amka', 'landline_phone', 'mobile_phone'];
  Object.keys(processedPatientData).forEach(key => {
    const value = processedPatientData[key as keyof typeof processedPatientData];
    // Only remove if undefined AND not in fields to preserve, or if empty string and not in fields to preserve
    if (value === undefined && !fieldsToPreserve.includes(key)) {
      delete processedPatientData[key as keyof typeof processedPatientData];
    } else if (value === '' && !fieldsToPreserve.includes(key)) {
      delete processedPatientData[key as keyof typeof processedPatientData];
    }
    // Keep fields to preserve even if they're empty strings (but still remove if undefined)
    else if (value === undefined && fieldsToPreserve.includes(key)) {
      delete processedPatientData[key as keyof typeof processedPatientData];
    }
  });
  

  
  const res = await apiClient.post(
    '/admin/patients/send-welcome-with-registration',
    {
      registration_data: {
        patient_data: processedPatientData,
        language: language
      },
      doctor_name: doctorName,
      clinic_name: clinicName,
      estimated_duration: estimatedDuration
    },
    {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    }
  );
  
  if (res.data.data) {
    return res.data.data;
  }
  
  throw new Error(res.data.message || 'Failed to send welcome email with registration');
}

// Public registration API functions (no auth required)

export async function validateRegistrationToken(registrationToken: string): Promise<RegistrationValidation> {
  const res = await apiClient.get(`/api/patient-registration/${registrationToken}/validate`);
  
  if (res.data.data) {
    return res.data.data;
  }
  
  throw new Error(res.data.message || 'Failed to validate registration token');
}

export async function saveRegistrationData(
  registrationToken: string,
  patientData: Partial<RegistrationFormData>,
  section?: string
): Promise<{ success: boolean; message: string; form_progress?: Record<string, boolean> }> {
  const res = await apiClient.post(
    `/api/patient-registration/${registrationToken}/save`,
    {
      patient_data: patientData,
      section: section
    }
  );
  
  if (res.data.data) {
    return {
      success: res.data.data.success,
      message: res.data.message,
      form_progress: res.data.data.form_progress
    };
  }
  
  throw new Error(res.data.message || 'Failed to save registration data');
}

export async function completeRegistration(
  registrationToken: string,
  finalData: RegistrationFormData
): Promise<{ success: boolean; message: string; patient_id?: string }> {
  const res = await apiClient.post(
    `/api/patient-registration/${registrationToken}/complete`,
    {
      patient_data: finalData
    }
  );
  
  if (res.data.data) {
    return {
      success: res.data.data.success,
      message: res.data.message,
      patient_id: res.data.data.patient_id
    };
  }
  
  throw new Error(res.data.message || 'Failed to complete registration');
}

export async function getRegistrationStatus(registrationToken: string): Promise<RegistrationStatus> {
  const res = await apiClient.get(`/api/patient-registration/${registrationToken}/status`);
  
  if (res.data.data) {
    return res.data.data;
  }
  
  throw new Error(res.data.message || 'Failed to get registration status');
}

// Secure Registration Link Generation (similar to questionnaire links)
export interface SecureRegistrationLink {
  display_token: string;
  link: string;
  link_expires_at: string;
  patient_id: string;
}

export async function generateSecureRegistrationLink(patientData: PatientInput, language: string = 'el'): Promise<SecureRegistrationLink> {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await apiClient.post('/admin/patients/generate-secure-registration-link', {
    patient_data: patientData,
    language: language
  }, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.data?.data) {
    const errorData = response.data;
    throw new Error(errorData.detail || errorData.message || `Failed to generate secure registration link: ${response.status}`);
  }

  return response.data.data;
}

// Questionnaire Link Generation

// NEW OTP-ON-DEMAND ARCHITECTURE
export async function generateSecureQuestionnaireLink(questionnaireId: string, patientId: string): Promise<QuestionnaireEmailData> {
  const response = await apiClient.post(`/admin/patients/${patientId}/questionnaires/${questionnaireId}/generate-link-on-demand`);
  
  if (!response.data?.data) {
    throw new Error(response.data?.detail || 'Failed to generate secure questionnaire link');
  }

  const data = response.data.data;
  
  // Return data in the format expected by the email system
  return {
    questionnaire_id: data.questionnaire_id || data.questionnaire_type || questionnaireId,
    name: data.name || `Questionnaire ${questionnaireId.toUpperCase()}`,
    link: data.link,
    description: `Secure questionnaire link - ${data.name || questionnaireId}`
    // Note: No OTP - it will be generated when patient clicks the link
  };
}

// Update questionnaire token target email and language
export async function updateQuestionnaireTokenTargetEmail(displayToken: string, targetEmail: string, language: string = 'el'): Promise<{ success: boolean; message: string }> {
  const response = await apiClient.post(`/admin/questionnaire-tokens/${displayToken}/update-target-email`, {
    target_email: targetEmail,
    language: language
  });
  
  if (!response.data?.data) {
    throw new Error(response.data?.detail || 'Failed to update target email');
  }
  
  return {
    success: response.data.data.updated,
    message: response.data.message
  };
}

// NEW: OTP Generation & Email API Functions for On-Demand System
export interface OTPGenerationRequest {
  display_token: string;
  language?: string;
}

export interface OTPGenerationResponse {
  success: boolean;
  message: string;
  otp_expires_at: string;
  email_sent: boolean;
  sent_to?: string;
}

/**
 * Generate and send OTP when patient first accesses a registration link
 * This is called automatically by the backend when the link is accessed
 * Frontend typically won't call this directly, but it's available for manual triggering
 */
export async function generateAndSendRegistrationOTP(
  displayToken: string, 
  language: string = 'el'
): Promise<OTPGenerationResponse> {
  const response = await apiClient.post(`/api/patient-registration/${displayToken}/generate-otp`, {
    language: language
  });
  
  if (!response.data?.data) {
    throw new Error(response.data?.detail || response.data?.message || 'Failed to generate and send OTP');
  }
  
  return response.data.data;
}

/**
 * Resend OTP email if patient didn't receive it
 * Can be called from frontend if user requests OTP resend
 */
export async function resendRegistrationOTP(
  displayToken: string, 
  language: string = 'el'
): Promise<OTPGenerationResponse> {
  const response = await apiClient.post(`/api/patient-registration/${displayToken}/resend-otp`, {
    language: language
  });
  
  if (!response.data?.data) {
    throw new Error(response.data?.detail || response.data?.message || 'Failed to resend OTP');
  }
  
  return response.data.data;
}

// NEW: Link Access Response Interface for Two-Step OTP System
export interface LinkAccessResponse {
  access_type: 'form' | 'otp_required';
  message: string;
  // For access_type: 'form' (already has valid session)
  session_id?: string;
  patient_id?: string;
  patient_data?: any;
  form_progress?: Record<string, boolean>;
  language?: string;
  // For access_type: 'otp_required' (needs OTP verification)
  otp_sent?: boolean;
  otp_expires_at?: string;
  sent_to?: string;
}

/**
 * Access registration link - handles the two-step process automatically
 * Step 1: Patient visits link 
 * Step 2: Backend generates OTP and sends email, returns requirement for OTP input
 * Step 3: Patient enters OTP to get access to form
 */
export async function accessRegistrationLink(displayToken: string): Promise<LinkAccessResponse> {
  const response = await apiClient.get(`/api/patient-registration/${displayToken}/access`);
  
  if (!response.data?.data) {
    throw new Error(response.data?.detail || response.data?.message || 'Failed to access registration link');
  }
  
  return response.data.data;
}

// Session-based Registration API Functions
export interface RegistrationSessionData {
  session_id: string;
  patient_id: string;
  patient_data: any;
  form_progress: Record<string, boolean>;
  expires_at: string;
  language: string;
}

export async function getRegistrationFormBySession(sessionId: string): Promise<RegistrationSessionData> {
  const response = await apiClient.get(`/api/patient-registration/session/${sessionId}`);
  
  if (!response.data?.data) {
    throw new Error(response.data?.detail || response.data?.message || 'Failed to get registration form data');
  }
  
  return response.data.data;
}

export async function saveRegistrationFormBySession(
  sessionId: string, 
  formData: any, 
  section?: string
): Promise<{ success: boolean; form_progress: Record<string, boolean> }> {
  const response = await apiClient.post(`/api/patient-registration/session/${sessionId}/save`, {
    form_data: formData,
    section: section
  });
  
  if (!response.data?.data) {
    throw new Error(response.data?.detail || response.data?.message || 'Failed to save registration form');
  }
  
  return response.data.data;
}

export async function completePatientRegistration(
  sessionId: string, 
  finalData: any
): Promise<{ success: boolean; patient_id: string }> {
  const response = await apiClient.post(`/api/patient-registration/session/${sessionId}/complete`, {
    patient_data: finalData
  });
  
  if (!response.data?.data) {
    throw new Error(response.data?.detail || response.data?.message || 'Failed to complete registration');
  }
  
  return response.data.data;
}

export async function verifyRegistrationOTP(
  displayToken: string, 
  otp: string
): Promise<{ patient_id: string; patient_data: any; language: string }> {
  const response = await apiClient.post(`/api/patient-registration/${displayToken}/verify`, {
    otp: otp
  });
  
  if (!response.data?.data) {
    throw new Error(response.data?.detail || response.data?.message || 'Failed to verify OTP');
  }
  
  return response.data.data;
}

// Patient Registration Response API
export interface PatientRegistrationResponse {
  patient_id: string;
  form_data: any;
  status: string;
  completion_percentage: number;
  created_at: string;
  updated_at?: string;
  completed_at?: string;
  language: string;
  section_progress?: Array<{
    section_id: string;
    completed: boolean;
    last_updated?: string;
  }>;
}

export async function getPatientRegistrationResponse(patientId: string): Promise<PatientRegistrationResponse> {
  const response = await apiClient.get(`/patients/${patientId}/registration-response`);
  
  if (!response.data?.data) {
    throw new Error(response.data?.detail || response.data?.message || 'Failed to get patient registration response');
  }
  
  return response.data.data;
}

// Registration GDPR Consent API
export interface RegistrationGDPRConsentRequest {
  patient_id: string;
  registration_token: string;
  consent_given: boolean;
}

export interface RegistrationGDPRConsentResponse {
  has_consent: boolean;
  consent_date?: string;
}

export async function storeRegistrationGDPRConsent(
  patientId: string,
  registrationToken: string,
  consentGiven: boolean
): Promise<{ success: boolean; message: string }> {
  const response = await apiClient.post('/api/registration/gdpr/consent', {
    patient_id: patientId,
    registration_token: registrationToken,
    consent_given: consentGiven
  });
  
  if (!response.data?.data?.success) {
    throw new Error(response.data?.detail || response.data?.message || 'Failed to store registration GDPR consent');
  }
  
  return response.data.data;
}

export async function getRegistrationGDPRConsent(
  patientId: string,
  registrationToken: string
): Promise<RegistrationGDPRConsentResponse | null> {
  try {
    const response = await apiClient.get(`/api/registration/gdpr/consent/${patientId}/${registrationToken}`);
    return response.data?.data || null;
  } catch (error: any) {
    // Return null if consent not found (404), otherwise throw error
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
}